import { Link, useLocation } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { useNavigate } from "react-router-dom";
import {
  DashboardOutlined,
  FileTextOutlined,
  MessageOutlined,
  DatabaseOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
  SearchOutlined,
  BarChartOutlined,
  SafetyOutlined,
  BankOutlined,
  GlobalOutlined,
  TrophyOutlined,
  ControlOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import logoShort from "../assets/logoShort.png";
import { clearAuthData } from "../utils/roleUtils";
import apiClient from "../services/apiClient";

const ModernSidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearUserData } = useUser();

  const { userData } = useUser();

  // Check if user is external (only EXTERNAL_USER role)
  const isExternalUser = () => {
    if (!userData || !userData.roles || userData.roles.length === 0) {
      return true; // Default to external if no roles
    }

    // Check if user has EXTERNAL_USER role
    return userData.roles.includes('EXTERNAL_USER');
  };

  const navigationItems = [
    {
      section: "Main",
      items: [
        {
          name: "Dashboard",
          path: "/d/dash",
          icon: <DashboardOutlined />,
        },
        {
          name: "Issuers",
          path: "/d/issuer-list",
          icon: <BankOutlined />,
        },
        {
          name: "ISINs",
          path: "/d/isin-list",
          icon: <BankOutlined />,
        },
        {
          name: "Financials",
          path: "/d/issuer-financial-reports",
          icon: <BarChartOutlined />,
        },
      ],
    },
    // Show Content section only for external users
    ...(isExternalUser() ? [{
      section: "Content",
      items: [
        {
          name: "Blogs",
          path: "/d/blogs",
          icon: <FileTextOutlined />,
        },
        {
          name: "Testimonials",
          path: "/d/testimonials",
          icon: <MessageOutlined />,
        },
        {
          name: "News",
          path: "/d/news",
          icon: <GlobalOutlined />,
        },
      ],
    }] : []),
    {
      section: "Data & Analytics",
      items: [
        {
          name: "NSDL",
          path: "/d/nsdl",
          icon: <DatabaseOutlined />,
        },
        {
          name: "Promoter Scores",
          path: "/promoter-scores",
          icon: <SafetyOutlined />,
        },
      ],
    },
  ];

  const footerItems = [
    {
      name: "Pricing",
      path: "/d/pricing",
      icon: <CreditCardOutlined />,
    },
    {
      name: "Settings",
      path: "/d/settings",
      icon: <SettingOutlined />,
    },
    {
      name: "Profile",
      path: "/d/profile",
      icon: <UserOutlined />,
    },
  ];

  const isActive = (path) => {
    if (path === "/d/blogs") {
      return (
        location.pathname === "/d/blogs" ||
        location.pathname.startsWith("/d/blogs/") ||
        location.pathname.startsWith("/d/blog-details/")
      );
    }

    // Handle Issuers section - should be active for issuer-list and issuer/details
    if (path === "/d/issuer-list") {
      return (
        location.pathname === "/d/issuer-list" ||
        location.pathname.startsWith("/d/issuer/details")
      );
    }

    // Handle ISINs section - should be active for isin-list and isin-details
    if (path === "/d/isin-list") {
      return (
        location.pathname === "/d/isin-list" ||
        location.pathname.startsWith("/d/isin-details/")
      );
    }

    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  return (
    <div className={`modern-sidebar ${collapsed ? "collapsed" : ""}`}>
      {/* Sidebar Header */}
      <div className="sidebar-header">
        <div className="logo-container">
          <img src={logoShort} alt="SimplyBond" className="logo" />
          {!collapsed && <span className="logo-text">SimplyBond</span>}
        </div>
        {!collapsed && (
          <button
            className="toggle-sidebar-btn"
            onClick={onToggle}
            title="Collapse sidebar"
          >
            <SearchOutlined />
          </button>
        )}
      </div>

      {/* Scrollable Navigation Area */}
      <div className="sidebar-nav-container">
        <nav className="sidebar-nav">
          {navigationItems.map((section, sectionIndex) => (
            <div key={sectionIndex} className="nav-section">
              {!collapsed && (
                <div className="nav-section-title">{section.section}</div>
              )}
              {section.items.map((item, itemIndex) => (
                <Link
                  key={itemIndex}
                  to={item.path}
                  className={`nav-item ${isActive(item.path) ? "active" : ""}`}
                  title={collapsed ? item.name : undefined}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {!collapsed && <span className="nav-text">{item.name}</span>}
                </Link>
              ))}
            </div>
          ))}
        </nav>
      </div>

      {/* Fixed Footer */}
      <div className="sidebar-footer">
        {footerItems.map((item, index) => (
          <Link
            key={index}
            to={item.path}
            className={`nav-item ${isActive(item.path) ? "active" : ""}`}
            title={collapsed ? item.name : undefined}
          >
            <span className="nav-icon">{item.icon}</span>
            {!collapsed && <span className="nav-text">{item.name}</span>}
          </Link>
        ))}

        {/* Logout Button */}
        <button
          className="nav-item logout-btn"
          onClick={() => {
            (async () => {
              try {
                const baseUrl = process.env.REACT_APP_AUTH_URL || "";
                await apiClient.post(`${baseUrl}/user/logout`);
              } catch (e) {
                // Ignore errors, proceed with client-side cleanup
              } finally {
                clearAuthData(navigate, clearUserData);
              }
            })();
          }}
          title={collapsed ? "Logout" : undefined}
        >
          <span className="nav-icon">
            <LogoutOutlined />
          </span>
          {!collapsed && <span className="nav-text">Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default ModernSidebar;
